import React, { useEffect } from 'react'
import { Container } from 'src/components/common/Container'
import styled, { css } from 'styled-components'
import { Typography } from 'src/components/common/Typography'
import {
  Noto_body1_b16,
  Noto_body1_r16,
  Noto_body2_b14,
  Noto_body2_r14,
} from 'src/styles/fonts/NotoSansKrFonts'
import Marquee from 'react-fast-marquee'
import GiftReceiveMsgCard from 'src/pages/GiftCardPage/GiftReceivePage/_components/GiftReceiveMsgCard'
import { ReactHookFc } from 'src/components/common/ReactHookForm'
import { GiftReceivePageProps } from 'src/pages/GiftCardPage/GiftReceivePage/index'
import Button, { ButtonOption3, ButtonOption7 } from 'src/components/common/Button'
import { useFormContext } from 'react-hook-form'
import { GiftReceiveFormItemsType } from 'src/types/react-hook-form'
import { maskPhoneNumber } from 'src/utils'
import RenderIf from 'src/components/common/RenderIf'
import { DateUtils } from 'src/utils/date'
import { useCountdown } from 'src/hooks/useCountDown.hook'
import GiftHeader from 'src/components/layout/GiftHeader/view'
import { ZIndex } from 'src/styles/mixin'

const GiftReceivePageView = (props: GiftReceivePageProps) => {
  const {
    registerAttribute,
    inputLock,
    handleInputCheck,
    handleGiftAccept,
    handleGiftReject,
    giftDetailData,
  } = props

  const { getDayDifference, getAfter7Days } = DateUtils
  const { countStart, count } = useCountdown({
    countExpiredCallbackFn: () => {},
  })

  const { recipientPNumber, termsAgree } = registerAttribute
  const {
    formState: { errors, isValid },
  } = useFormContext<GiftReceiveFormItemsType>()
  const { recipientPNumber: { message: recipientPNumberErrMsg } = {} } = errors

  const { SendType, RecipientPNumber, RegDT } = giftDetailData
  const isKakaoSend = SendType === 0
  const formNotiMsg = isKakaoSend
    ? inputLock
      ? '휴대폰 번호를 꼭! 확인해주세요.'
      : recipientPNumberErrMsg
    : '선물 하신분이 입력한 번호입니다.'
  const after7Days = getAfter7Days(RegDT).endOf('day')

  useEffect(() => {
    countStart(Number(getDayDifference(after7Days, 'seconds')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <S.GiftReceiveWrap>
      <GiftHeader title="선물받기" isFull={true} />
      <S.ExpireNotiContainer>
        <S.ExpireNotiAnimation>
          {Number(getDayDifference(after7Days, 'seconds')) >= 60 * 60 * 24 ? (
            <>
              <Typography $color="#ff6816">
                {after7Days.format('MM.DD').split('.')[0]}월{' '}
                {after7Days.format('MM.DD').split('.')[1]}일
              </Typography>
              까지 선물을 받아주세요.
            </>
          ) : (
            <>
              <Typography $style={S.ExpireTime()}>{count}</Typography>후에 자동으로 선물이 거절되며,
              선물을 보낸 분께 알림이 발송됩니다.
            </>
          )}
        </S.ExpireNotiAnimation>
      </S.ExpireNotiContainer>
      <GiftReceiveMsgCard {...props} />
      <S.GiftReceiveForm $option="flex_column">
        <S.GiftReceivePNumContainer $option="flex_column" $row_gap="2px">
          <Typography $fontStyle={Noto_body1_b16} $color="var(--black-90)">
            {isKakaoSend ? '선물받을 전화번호 입력' : '선물 받는분'}
          </Typography>
          <Typography $fontStyle={Noto_body2_r14} $color="var(--black-50)">
            쿠폰 유효기간 만료, 환불 안내가 진행됩니다.
          </Typography>
          <S.FormInputWrap $isKakaoSend={isKakaoSend}>
            <ReactHookFc.Input
              {...recipientPNumber}
              style={S.FormInput(isKakaoSend, inputLock)}
              disabled={!isKakaoSend || inputLock}
            />
            <RenderIf when={!isKakaoSend}>
              <S.FormInputFakeBox>{maskPhoneNumber(RecipientPNumber)}</S.FormInputFakeBox>
            </RenderIf>
            <RenderIf when={isKakaoSend}>
              <Button
                option={ButtonOption7('default')}
                onClickFn={handleInputCheck}
                style={S.FormButton()}
              >
                {inputLock ? '변경' : '확인'}
              </Button>
            </RenderIf>
          </S.FormInputWrap>
          <S.FormNotiMsg $isValid={!recipientPNumberErrMsg}>{formNotiMsg}</S.FormNotiMsg>
        </S.GiftReceivePNumContainer>
        <S.GiftReceiveBtnContainer>
          <S.FormCheckBoxWrap $option="flex_row" $column_gap="8px">
            <ReactHookFc.CheckBox
              {...termsAgree}
              style={S.FormCheckBox()}
              bottomChildren={
                <Container $option="flex_row" $column_gap="4px">
                  <S.FormCheckBoxLable>{termsAgree?.labelValue}</S.FormCheckBoxLable>
                  <S.FormCheckBoxViewMore onClick={termsAgree?.labelOnClick}>
                    자세히 보기
                  </S.FormCheckBoxViewMore>
                </Container>
              }
            />
          </S.FormCheckBoxWrap>
          <Button
            option={ButtonOption3('active')}
            onClickFn={handleGiftAccept}
            disabled={!isValid || !inputLock}
          >
            선물받기
          </Button>
          <S.GiftReceiveRejectBtn onClick={handleGiftReject}>선물 거절하기</S.GiftReceiveRejectBtn>
        </S.GiftReceiveBtnContainer>
      </S.GiftReceiveForm>
    </S.GiftReceiveWrap>
  )
}
export default GiftReceivePageView

const S = {
  GiftReceiveWrap: styled(Container)`
    margin-top: 94px;
    min-width: 320px;
  `,
  ExpireNotiContainer: styled(Container)`
    position: fixed;
    top: 50px;
    padding: 10px 8px;
    background-color: var(--black-90);
    z-index: ${ZIndex.TopMenu};
  `,
  ExpireNotiAnimation: styled(Marquee)`
    ${Noto_body2_r14};
    color: var(--black-20);
  `,
  ExpireTime: () => css`
    color: #ff6816;
    margin: 0 4px 0 10px;
  `,
  GiftReceiveForm: styled(Container)`
    align-items: center;
    background-color: var(--white);
  `,
  GiftReceivePNumContainer: styled(Container)`
    max-width: 360px;
    width: 100%;
    padding: 30px 20px 40px;
    border-bottom: 1px solid var(--black-6);
  `,
  GiftReceivePNumInput: styled(Container)`
    padding: 30px 20px 40px;
  `,
  FormInputWrap: styled(Container)<{ $isKakaoSend: boolean }>`
    display: grid;
    grid-template-columns: ${({ $isKakaoSend }) => ($isKakaoSend ? '1fr 73px' : '1fr')};
    margin: 8px 0 10px 0;
    gap: ${({ $isKakaoSend }) => ($isKakaoSend ? '6px' : '0')};
  `,
  FormInput: ($isKakaoSend: boolean, $inputLock: boolean) => css`
    ${Noto_body1_r16};
    padding: 12px 16px;
    height: 48px;
    border-bottom: 1px solid var(--black-20);
    background-color: ${$inputLock ? 'var(--black-6)' : 'var(--white)'};
    ${!$isKakaoSend && 'display: none'};
  `,
  FormInputFakeBox: styled(Container)`
    ${Noto_body1_r16};
    padding: 12px 16px;
    height: 48px;
    border-bottom: 1px solid var(--black-20);
    background-color: var(--black-6);
    text-align: center;
  `,
  FormButton: () => css`
    width: 73px;
  `,
  FormNotiMsg: styled(Container)<{ $isValid?: boolean }>`
    ${Noto_body2_r14};
    color: ${({ $isValid }) => (!!$isValid ? 'var(--black-90)' : 'var(--red-50)')};
  `,
  GiftReceiveBtnContainer: styled(Container)`
    padding: 23px 20px 70px;
    max-width: 360px;
    width: 100%;
  `,
  FormCheckBoxWrap: styled(Container)`
    margin-bottom: 16px;
    align-items: center;
  `,
  FormCheckBox: () => css`
    transform: scale(0.8);
  `,
  FormCheckBoxLable: styled(Typography)`
    ${Noto_body2_r14};
    width: fit-content;
    color: var(--black-90);
  `,
  FormCheckBoxViewMore: styled.button`
    ${Noto_body2_b14};
    width: fit-content;
    color: var(--black-50);
    text-decoration: underline;
  `,
  GiftReceiveRejectBtn: styled.button`
    ${Noto_body2_b14};
    margin-top: 21px;
    color: var(--black-50);
  `,
}
